/**
 * WebSocket Context for managing real-time connections
 */

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import webSocketService from '../service/WebSocketService';

const WebSocketContext = createContext();

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};

export const WebSocketProvider = ({ children, hotelId, authToken }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);

  // Initialize WebSocket connection
  const initializeConnection = useCallback(async () => {
    if (!hotelId || !authToken) {
      return;
    }

    try {
      setConnectionError(null);
      
      await webSocketService.connect(hotelId, authToken);
      setIsConnected(true);
      
      // Check if already authenticated (in case of reconnection)
      if (webSocketService.getAuthenticationStatus()) {
        setIsAuthenticated(true);
      }
      
    } catch (error) {
      console.error('WebSocket: Connection failed:', error);
      setConnectionError(error.message);
      setIsConnected(false);
    }
  }, [hotelId, authToken]);

  // Set up event listeners
  useEffect(() => {
    const handleAuthenticated = () => {
      setIsAuthenticated(true);
      setConnectionError(null);
    };

    const handleAuthError = (data) => {
      console.error('WebSocket: Authentication failed:', data.message);
      setIsAuthenticated(false);
      setConnectionError(data.message);
    };

    const handleReconnected = () => {
      setIsConnected(true);
      setConnectionError(null);
      setReconnectAttempts(0);
    };

    const handleMaxReconnectAttempts = (data) => {
      console.error('WebSocket: Max reconnection attempts reached:', data.attempts);
      setConnectionError('Connection lost. Please refresh the page.');
      setReconnectAttempts(data.attempts);
    };

    const handleError = (data) => {
      console.error('WebSocket: Error:', data.message);
      setConnectionError(data.message);
    };

    // Subscribe to WebSocket events
    webSocketService.on('authenticated', handleAuthenticated);
    webSocketService.on('auth_error', handleAuthError);
    webSocketService.on('reconnected', handleReconnected);
    webSocketService.on('max_reconnect_attempts', handleMaxReconnectAttempts);
    webSocketService.on('error', handleError);

    return () => {
      // Cleanup event listeners
      webSocketService.off('authenticated', handleAuthenticated);
      webSocketService.off('auth_error', handleAuthError);
      webSocketService.off('reconnected', handleReconnected);
      webSocketService.off('max_reconnect_attempts', handleMaxReconnectAttempts);
      webSocketService.off('error', handleError);
    };
  }, []);

  // Initialize connection when hotelId or authToken changes
  useEffect(() => {
    if (hotelId && authToken) {
      initializeConnection();
    }

    // Cleanup on unmount or when dependencies change
    return () => {
      if (!hotelId || !authToken) {
        webSocketService.disconnect();
        setIsConnected(false);
        setIsAuthenticated(false);
      }
    };
  }, [hotelId, authToken, initializeConnection]);

  // Check WebSocket auth status periodically to catch state changes
  useEffect(() => {
    if (!isConnected) return;

    const checkAuthStatus = () => {
      const authStatus = webSocketService.getAuthenticationStatus();
      if (authStatus !== isAuthenticated) {
        setIsAuthenticated(authStatus);
      }
    };

    // Check immediately and then periodically
    checkAuthStatus();
    const intervalId = setInterval(checkAuthStatus, 1000);

    return () => clearInterval(intervalId);
  }, [isConnected, isAuthenticated]);

  // Update token when it changes
  useEffect(() => {
    if (authToken && isConnected) {
      webSocketService.updateToken(authToken);
    }
  }, [authToken, isConnected]);

  // Subscribe to reservation events
  const onReservationCreated = useCallback((callback) => {
    webSocketService.on('reservation_created', callback);
    return () => webSocketService.off('reservation_created', callback);
  }, []);

  const onReservationUpdated = useCallback((callback) => {
    webSocketService.on('reservation_updated', callback);
    return () => webSocketService.off('reservation_updated', callback);
  }, []);

  const onReservationCancelled = useCallback((callback) => {
    webSocketService.on('reservation_cancelled', callback);
    return () => webSocketService.off('reservation_cancelled', callback);
  }, []);

  // Subscribe to inventory events
  const onInventoryUpdated = useCallback((callback) => {
    webSocketService.on('inventory_updated', callback);
    return () => {
      webSocketService.off('inventory_updated', callback);
    };
  }, []);

  // Manual reconnection
  const reconnect = useCallback(() => {
    if (hotelId && authToken) {
      webSocketService.disconnect();
      setIsConnected(false);
      setIsAuthenticated(false);
      setConnectionError(null);
      initializeConnection();
    }
  }, [hotelId, authToken, initializeConnection]);

  const contextValue = {
    isConnected,
    isAuthenticated,
    connectionError,
    reconnectAttempts,
    onReservationCreated,
    onReservationUpdated,
    onReservationCancelled,
    onInventoryUpdated,
    reconnect,
    webSocketService // Expose service for direct access if needed
  };

  return (
    <WebSocketContext.Provider value={contextValue}>
      {children}
    </WebSocketContext.Provider>
  );
};

export default WebSocketProvider;